import { useEffect, useRef, useState } from "react";
import { Trash2, X } from "lucide-react";
import clsx from "clsx";
import type { Editor } from "@tiptap/react";
import type { Accent, Card } from "../lib/types";
import { ACCENTS, accentClasses } from "../lib/types";
import {
  FORMAT_ACTIONS,
  isShortcutsMenuKey,
  type FormatAction,
} from "../lib/format-actions";
import { ShortcutsMenu } from "./ShortcutsMenu";
import { RichEditor } from "./RichEditor";

type Props = {
  card: Card;
  fallbackAccent: Accent;
  onClose: () => void;
  onSave: (patch: Partial<Pick<Card, "title" | "body" | "accent">>) => void;
  onDelete: () => void;
};

export function CardModal({ card, fallbackAccent, onClose, onSave, onDelete }: Props) {
  const [title, setTitle] = useState(card.title);
  const [body, setBody] = useState(card.body);
  const [accent, setAccent] = useState<Accent | null>(card.accent ?? null);
  const titleRef = useRef<HTMLTextAreaElement | null>(null);
  const editorRef = useRef<Editor | null>(null);
  const dirty = useRef(false);

  // Shortcuts menu state. `position: "center"` for Cmd+/, `{x,y}` for right-
  // click. `null` = closed.
  const [menu, setMenu] = useState<
    null | { position: "center" | { x: number; y: number } }
  >(null);

  const applyAction = (a: FormatAction) => {
    const editor = editorRef.current;
    if (!editor) return;
    a.apply(editor);
  };

  // Track changes so we only persist if something actually moved.
  useEffect(() => {
    dirty.current =
      title !== card.title ||
      body !== card.body ||
      (accent ?? null) !== (card.accent ?? null);
  }, [title, body, accent, card.title, card.body, card.accent]);

  useEffect(() => {
    titleRef.current?.focus();
    titleRef.current?.setSelectionRange(title.length, title.length);
    // intentionally only on mount
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, []);

  const close = () => {
    if (dirty.current) {
      onSave({ title: title.trim() || "Untitled", body, accent });
    }
    onClose();
  };

  useEffect(() => {
    const handler = (e: KeyboardEvent) => {
      // Esc and Cmd+Enter close (and save) the modal — UNLESS the shortcuts
      // menu is the active overlay, in which case it handles its own keys.
      if (menu) return;
      if (e.key === "Escape") close();
      if ((e.metaKey || e.ctrlKey) && e.key === "Enter") close();
    };
    window.addEventListener("keydown", handler);
    return () => window.removeEventListener("keydown", handler);
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [title, body, accent, menu]);

  // Global keyboard handler on the modal: pick up Cmd+/ from anywhere
  // (including inside the editor) and route format shortcuts that Tiptap
  // doesn't already bind for us.
  useEffect(() => {
    const handler = (e: KeyboardEvent) => {
      if (menu) return;
      const editor = editorRef.current;
      if (!editor || !editor.isFocused) return;
      if (isShortcutsMenuKey(e)) {
        e.preventDefault();
        setMenu({ position: "center" });
        return;
      }
      // Only intercept the actions Tiptap's keymap doesn't already cover —
      // bold/italic/lists/headings are handled inside the editor itself
      // (and we wouldn't want to double-apply them).
      for (const action of FORMAT_ACTIONS) {
        if (action.builtin) continue;
        if (action.matchKey(e)) {
          e.preventDefault();
          applyAction(action);
          return;
        }
      }
    };
    document.addEventListener("keydown", handler);
    return () => document.removeEventListener("keydown", handler);
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [menu]);

  const cls = accentClasses[accent ?? fallbackAccent];

  return (
    <div
      className="fixed inset-0 z-50 flex items-start justify-center bg-ink/10 px-4 pt-[12vh] backdrop-blur-sm"
      onClick={close}
    >
      <div
        className={clsx(
          "relative flex max-h-[80vh] w-full max-w-2xl flex-col overflow-hidden rounded-lane bg-surface shadow-modal",
          "border border-border"
        )}
        onClick={(e) => e.stopPropagation()}
      >
        {/* Accent strip on top */}
        <div className={clsx("h-1 w-full shrink-0", cls.dot)} />

        <div className="shrink-0 px-7 pt-6">
          <div className="flex items-start justify-between gap-4">
            <textarea
              ref={titleRef}
              value={title}
              onChange={(e) => setTitle(e.target.value)}
              onKeyDown={(e) => {
                if (e.key === "Enter" && !e.shiftKey) {
                  e.preventDefault();
                  editorRef.current?.commands.focus("end");
                }
              }}
              rows={1}
              className="input-bare flex-1 resize-none text-xl font-medium tracking-tight leading-snug"
              placeholder="title"
              style={{ height: "auto" }}
              onInput={(e) => {
                const el = e.currentTarget;
                el.style.height = "auto";
                el.style.height = Math.min(el.scrollHeight, 120) + "px";
              }}
            />
            <button
              onClick={close}
              className="shrink-0 rounded-md p-1 text-ink-mute transition-colors hover:bg-canvas hover:text-ink"
              aria-label="close"
            >
              <X size={16} />
            </button>
          </div>
        </div>

        <div className="mt-3 min-h-0 overflow-y-auto px-7 pb-4">
          <RichEditor
            initialMarkdown={card.body}
            onChange={setBody}
            onReady={(editor) => {
              editorRef.current = editor;
            }}
            onContextMenu={(e) => {
              setMenu({ position: { x: e.clientX, y: e.clientY } });
            }}
            className="min-h-[140px]"
          />
        </div>

        <div className="flex shrink-0 items-center justify-between border-t border-border bg-canvas/40 px-7 py-3">
          <div className="flex items-center gap-1.5">
            <span className="mr-2 text-[10px] uppercase tracking-wider text-ink-mute">
              color
            </span>
            <button
              onClick={() => setAccent(null)}
              className={clsx(
                "h-4 w-4 rounded-full border border-border-strong/40 bg-canvas transition-transform hover:scale-110",
                accent === null && "ring-2 ring-offset-1 ring-ink-faint"
              )}
              title="default"
            />
            {ACCENTS.map((a) => (
              <button
                key={a}
                title={a}
                onClick={() => setAccent(a)}
                className={clsx(
                  "h-4 w-4 rounded-full border border-black/5 transition-transform hover:scale-110",
                  accentClasses[a].dot,
                  accent === a && "ring-2 ring-offset-1 ring-ink-faint"
                )}
              />
            ))}
          </div>

          <div className="flex items-center gap-2">
            <button
              onClick={() => {
                // Card delete is soft (goes to Bin, recoverable for 30 days).
                // No confirm dialog — the surrounding ThreadView fires a
                // toast with Undo right after the delete lands.
                onDelete();
                onClose();
              }}
              className="flex items-center gap-1.5 rounded-md px-2 py-1 text-xs text-ink-mute transition-colors hover:bg-blush-50 hover:text-blush-400"
            >
              <Trash2 size={12} /> delete
            </button>
            <span className="font-mono text-[10px] text-ink-mute">
              <kbd className="rounded bg-surface border border-border px-1 py-0.5">esc</kbd> to save
              <span className="mx-1.5 opacity-40">·</span>
              <kbd className="rounded bg-surface border border-border px-1 py-0.5">⌘/</kbd> formatting
            </span>
          </div>
        </div>

        {menu && (
          <ShortcutsMenu
            position={menu.position}
            onClose={() => {
              setMenu(null);
              requestAnimationFrame(() => editorRef.current?.commands.focus());
            }}
            onApply={(a) => {
              setMenu(null);
              requestAnimationFrame(() => {
                editorRef.current?.commands.focus();
                applyAction(a);
              });
            }}
          />
        )}
      </div>
    </div>
  );
}
